import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const Admin = () => {
  const navigate = useNavigate();
  const [errMsg, setErrMsg] = useState("");
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  const axiosPrivate = useAxiosPrivate();

  const handleLogout = async (e) => {
    const confirmLogout = window.confirm("Are you sure you want to logout?");
    if (!confirmLogout) {
      return;
    }
    e.preventDefault();
    localStorage.removeItem("user");
    try {
      await axiosPrivate.get("/logout");
    } finally {
      navigate("/login");
    }
  };

  useEffect(() => {
    const getManagers = async () => {
      try {
        const response = await axiosPrivate.get("/admin/getmanagers");
        setManagers(response?.data);
      } catch (err) {
        console.error(err);
        setErrMsg(err?.response?.data?.message || err?.message);
      } finally {
        setLoading(false);
      }
    };
    getManagers();
  }, [axiosPrivate]);

  const handleUpdateCredits = async (username) => {
    const credits = prompt("Enter new credit amount for manager:");
    if (!credits) return;

    try {
      await axiosPrivate.post(
        "/admin/updatecredits",
        JSON.stringify({ manager: username, newcredits: credits })
      );

      // update setmanager with new credits
      setManagers((prev) =>
        prev.map((manager) => {
          if (manager.user === username) {
            return {
              ...manager,
              credits,
            };
          }
          return manager;
        })
      );
      alert("Credits updated successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to update credits");
    }
  };

  const handleDeleteManager = async (username) => {
    try {
      const confirmDelete = window.confirm(`Are you sure you want to delete the manager ${username}?`);
      if (!confirmDelete) {
        return;
      }

      await axiosPrivate.post(
        "/admin/deletemanager",
        JSON.stringify({ manager: username })
      );

      setManagers((prev) => prev.filter((manager) => manager.user !== username));
    } catch (err) {
      console.error(err);
      alert("Failed to delete manager");
    }
  };

  return (
    <div className="fade-in" style={{ maxWidth: "1400px", margin: "0 auto", padding: "2rem 1rem" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              marginBottom: "0.5rem",
              background: "linear-gradient(135deg, var(--primary-purple-light), var(--accent-cyan))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Admin Dashboard
          </h1>
          <p style={{ color: "var(--text-muted)", fontSize: "1.125rem" }}>
            Manage users, credits, and system settings
          </p>
        </div>
        <button className="btn-secondary" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {errMsg && <p className="errmsg">{errMsg}</p>}

      {/* Stats Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1.5rem", marginBottom: "3rem" }}>
        <div className="card" style={{ padding: "1.5rem", display: "flex", alignItems: "center", gap: "1rem" }}>
          <div style={{ fontSize: "2.5rem", background: "rgba(124, 58, 237, 0.1)", padding: "1rem", borderRadius: "12px" }}>👥</div>
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Managers</p>
            <p style={{ fontSize: "2rem", fontWeight: "700", color: "var(--text-primary)" }}>{managers.length}</p>
          </div>
        </div>

        <div className="card" style={{ padding: "1.5rem", display: "flex", alignItems: "center", gap: "1rem" }}>
          <div style={{ fontSize: "2.5rem", background: "rgba(6, 182, 212, 0.1)", padding: "1rem", borderRadius: "12px" }}>💳</div>
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Credits Issued</p>
            <p style={{ fontSize: "2rem", fontWeight: "700", color: "var(--text-primary)" }}>
              {managers.reduce((acc, curr) => acc + parseInt(curr.credits || 0), 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Managers Table */}
      <div style={{ marginBottom: "2rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: "600" }}>Registered Managers</h2>
        <button className="btn-primary" onClick={() => navigate("/admin/register")}>
          + Register New Manager
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem" }}>
          <div className="spin" style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
          <p style={{ color: "var(--text-secondary)" }}>Loading managers...</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: "16px", border: "1px solid var(--border)" }}>
          <table style={{ width: "100%", minWidth: "800px" }}>
            <thead>
              <tr>
                <th>Username/Email</th>
                <th>Name</th>
                <th>Credits</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {managers.map((manager) => (
                <tr key={manager.user}>
                  <td style={{ fontWeight: "600", color: "var(--text-primary)" }}>{manager.user}</td>
                  <td>{manager.name || "N/A"}</td>
                  <td>
                    <span style={{
                      padding: "0.25rem 0.75rem",
                      borderRadius: "9999px",
                      fontSize: "0.875rem",
                      background: "rgba(245, 158, 11, 0.1)",
                      border: "1px solid var(--warning-amber)",
                      color: "var(--warning-amber)"
                    }}>
                      {manager.credits}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: "0.75rem", padding: "0.5rem" }}
                        onClick={() => handleUpdateCredits(manager.user)}
                      >
                        Update Credits
                      </button>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: "0.75rem", padding: "0.5rem" }}
                        onClick={() => {
                          localStorage.setItem("manager", manager.user);
                          navigate(`/admin/getquizzes/?manager=${manager.user}`);
                        }}
                      >
                        View Quizzes
                      </button>
                      <button
                        className="btn-danger"
                        style={{ fontSize: "0.75rem", padding: "0.5rem" }}
                        onClick={() => handleDeleteManager(manager.user)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {managers.length === 0 && (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>
                    No managers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Admin;
